"use client";

import { Box, Typography } from "@mui/material";
import LastUpdate from "@/src/components/common/pages/last-update/last-update";
import Outlines from "@/src/components/common/outlines/outlines";
import ReactHtmlParser, { addHeadingIds } from "@/src/utils/html-parser";
import SocialIcons from "@/src/components/common/pages/social-icons/social-icons";
import ActionButton from "@/src/components/common/broadsheet/action-button";
import Prose from "@/src/components/common/broadsheet/prose";
import {
  doubleRule,
  FONT_DISPLAY,
  HEADING_SIZE,
  RAIL_RIGHT,
  SHEET_GUTTER,
  stickyRailSx,
} from "@/src/components/common/broadsheet/tokens";

export default function KnowledgeBaseComponent({
  journal,
  kb,
}: {
  journal: any;
  kb: any;
}) {
  const body = addHeadingIds(kb?.body || "");
  const hasOutline = /<h[2-6][^>]*\sid=/i.test(body);
  const indexHref = journal?.is_main_site
    ? "/knowledgebase"
    : `/journals/${journal?.path}/knowledgebase`;

  return (
    <Box
      className="kb-font"
      sx={{ px: SHEET_GUTTER, pb: { xs: 1.5, md: 2.5 } }}
    >
      <Box
        sx={{ pt: { xs: 1, md: 1.5 }, pb: { xs: 0.75, md: 1 }, ...doubleRule }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            justifyContent: "space-between",
            columnGap: 1,
            rowGap: 0.375,
          }}
        >
          {kb?.title ? (
            <Typography
              component="h1"
              id="kb-title"
              sx={{
                m: 0,
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: HEADING_SIZE.page,
                lineHeight: 1.08,
                letterSpacing: "0.01em",
                color: "custom.ink",
                maxWidth: "34ch",
              }}
            >
              {kb.title}
            </Typography>
          ) : null}
          <Box
            id="kb-updated-at"
            data-updated={kb?.updated_at}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flex: "none",
            }}
          >
            {kb?.updated_at ? <LastUpdate updatedAt={kb.updated_at} /> : null}
            <SocialIcons direction="column" />
          </Box>
        </Box>
      </Box>

      <Box
        id="kb-content"
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: hasOutline ? `1fr ${RAIL_RIGHT}` : "1fr",
          },
          columnGap: 2.5,
          rowGap: 1.25,
          alignItems: "start",
          "& > *": { minWidth: 0 },
        }}
      >
        <Box component="article" sx={{ pt: { xs: 1, md: 1.25 } }}>
          <Prose id="kb-body">{ReactHtmlParser(body)}</Prose>
          <Box sx={{ mt: 1.5 }}>
            <ActionButton href={indexHref} variant="outline" size="small">
              All knowledge base articles
            </ActionButton>
          </Box>
        </Box>

        {hasOutline ? (
          <Box
            sx={{
              ...stickyRailSx,
              display: { xs: "none", lg: "block" },
              pt: 1.25,
              pl: 1.25,
              borderLeft: "1px solid",
              borderColor: "custom.ruleHairline",
            }}
          >
            <Outlines content={body} hasAbstract={false} hasBody={false} />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
